import { LATENCY_SOURCE, publishLatencySample } from './networkLatencyObserver';

const DEFAULT_PROBE_INTERVAL_MS = 30_000;

const noop = (): void => {};

const getNow = (): number => {
  if (typeof performance !== 'undefined' && performance.now) {
    return performance.now();
  }

  return Date.now();
};

const getProbeUrl = (): string | undefined => {
  return import.meta.env.VITE_NETWORK_PROBE_URL;
};

const isProbeAllowed = (): boolean => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return false;
  }

  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    return false;
  }

  return true;
};

const runProbe = async (probeUrl: string): Promise<void> => {
  if (!isProbeAllowed()) {
    return;
  }

  const separator = probeUrl.includes('?') ? '&' : '?';
  const cacheBustedUrl = `${probeUrl}${separator}_probe=${Date.now()}`;
  const startTime = getNow();

  try {
    await fetch(cacheBustedUrl, { method: 'GET', cache: 'no-store' });
  } catch {
    return;
  }

  const networkLatencyMs = getNow() - startTime;

  publishLatencySample({
    durationMs: networkLatencyMs,
    networkLatencyMs,
    latencySource: LATENCY_SOURCE.PROBE,
    isTimeout: false,
    url: probeUrl,
  });
};

/**
 * Periodically issues a cache-busted request to a tiny static asset on the API
 * origin and records its round-trip as a backend-independent network latency
 * sample. A static asset is served with negligible backend time, so the
 * round-trip approximates the pure network RTT even over a warm keep-alive
 * connection. Opt-in through `VITE_NETWORK_PROBE_URL`; a safe no-op otherwise.
 */
export const startLatencyProbe = (
  intervalMs: number = DEFAULT_PROBE_INTERVAL_MS,
): (() => void) => {
  const probeUrl = getProbeUrl();

  if (!probeUrl || typeof window === 'undefined') {
    return noop;
  }

  let isProbeInFlight = false;

  const tick = async (): Promise<void> => {
    if (isProbeInFlight) {
      return;
    }

    isProbeInFlight = true;

    try {
      await runProbe(probeUrl);
    } finally {
      isProbeInFlight = false;
    }
  };

  const intervalId = window.setInterval(() => {
    void tick();
  }, intervalMs);

  return () => {
    window.clearInterval(intervalId);
  };
};
